import { KeyMetricsData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/KeyMetricsAdapter';
import { UtilizationData } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import { LengthBasedMetrics } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/ContentServerProxy/types';
import { ContentSectionId } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/hooks/ContentSectionId';
import { safeDivide } from 'src/utils/app';

export enum ContentUtilizationType {
  Title = 'Title',
  ItemHighlights = 'Item Highlights',
  Bullet = 'Bullet',
  Description = 'Description',
  Images = 'Images',
  Image = 'Image',
  Video = 'Video',
  EnhancedContent = 'Enhanced Content'
}

export enum ContentUtilizationTitles {
  Title = 'Titles',
  ItemHighlights = 'Item Highlights',
  Bullets = 'Bullet Points',
  Descriptions = 'Descriptions',
  Images = 'Images',
  Videos = 'Videos',
  Enhanced = 'Enhanced',
  TotalContentUtilization = 'Total Content Utilization'
}

export enum ContentAccuracyTitles {
  Titles = 'Titles',
  ItemHighlights = 'Item Highlights',
  BulletPoints = 'Bullet Points',
  Descriptions = 'Descriptions',
  Images = 'Images',
  Videos = 'Videos',
  Enhanced = 'Enhanced',
  TotalContentAccuracy = 'Total Content Accuracy'
}

// For now Content Utilization is only offered for Amazon US clients
// The team does not intend to offer content analysis for non-Amazon US clients yet
const amazonRetailerUtilizationTemplate = {
  title: 1,
  itemHighlights: 1,
  bullets: 5,
  description: 1,
  images: 6,
  videos: 1,
  enhanced: 1
};

const getAmazonRetailerUtilizationTemplate = (includeItemHighlights: boolean) => {
  if (includeItemHighlights) {
    return amazonRetailerUtilizationTemplate;
  }
  const { itemHighlights: _itemHighlights, ...rest } = amazonRetailerUtilizationTemplate;
  return rest;
};

interface ContentUtilizationData {
  title: ContentUtilizationTitles;
  primary: number;
  secondary: number;
  details: string;
  lowProducts: number;
  /** Title-only: count of products over the 75-char cap. */
  overLimitProducts?: number;
  /** Which drill-down link to show on the bar (majority wins; tie → over-limit). */
  preferredTitleLink?: 'lowUtilization' | 'overLimit';
  scrollToId: ContentSectionId;
}

/**
 * Get the missing utilization elements for a product
 * @param productUtilizationData - The utilization data for a product
 * If any criteria such as title, bullets, videos are missing, or contentUtilization is 0,
 * we consider it as missing.
 *
 * @returns The missing count and the name of the missing elements
 */
const getMissingUtilizationElements = (
  productUtilizationData: UtilizationData[],
  includeItemHighlights: boolean
): {
  missingCount: number;
  missingElements: string[];
} => {
  const availableContent = new Map<string, UtilizationData>();

  productUtilizationData.forEach((item) => {
    availableContent.set(item.title.toLowerCase(), item);
  });

  const missingElements: string[] = [];
  const template = getAmazonRetailerUtilizationTemplate(includeItemHighlights);
  const templateKeys = Object.keys(template);

  templateKeys.forEach((key) => {
    const requiredCount = template[key];
    if (requiredCount > 0) {
      switch (key) {
        case 'title':
          const titleData = availableContent.get('title');
          if (!titleData || titleData.currentUtilization === 0) {
            missingElements.push(ContentUtilizationType.Title);
          }
          break;

        case 'itemHighlights':
          const itemHighlightsData = availableContent.get('item highlights');
          if (!itemHighlightsData || itemHighlightsData.currentUtilization === 0) {
            missingElements.push(ContentUtilizationType.ItemHighlights);
          }
          break;

        case 'bullets':
          let missingBulletsCount = 0;
          for (let i = 1; i <= requiredCount; i++) {
            const bulletData = availableContent.get(`bullet ${i}`);
            if (!bulletData || bulletData.currentUtilization === 0) {
              missingBulletsCount++;
            }
          }
          if (missingBulletsCount > 0) {
            missingElements.push(`${missingBulletsCount} bullet(s)`);
          }
          break;

        case 'images':
          const imagesData = availableContent.get('images');
          if (!imagesData || imagesData.currentUtilization === 0) {
            missingElements.push(ContentUtilizationType.Images);
          } else if (imagesData.currentUtilization < requiredCount) {
            missingElements.push(
              `${ContentUtilizationType.Images} (has ${imagesData.currentUtilization}, needs ${requiredCount})`
            );
          }
          break;

        case 'description':
          const descriptionData = availableContent.get('description');
          if (!descriptionData || descriptionData.currentUtilization === 0) {
            missingElements.push(ContentUtilizationType.Description);
          }
          break;

        case 'videos':
          const videosData = availableContent.get('video');
          if (!videosData || videosData.currentUtilization === 0) {
            missingElements.push(ContentUtilizationType.Video);
          } else if (videosData.currentUtilization < requiredCount) {
            missingElements.push(`Videos (has ${videosData.currentUtilization}, needs ${requiredCount})`);
          }
          break;

        case 'enhanced':
          const enhancedData =
            availableContent.get('enhanced content') ||
            productUtilizationData.find(
              (item) => item.title.toLowerCase().includes('enhanced') || item.type === 'aPlusContent'
            );
          if (!enhancedData || !enhancedData.currentUtilization) {
            missingElements.push(ContentUtilizationType.EnhancedContent);
          }
          break;
      }
    }
  });

  return {
    missingCount: missingElements.length,
    missingElements
  };
};

export const formatContentUtilizationData = (
  rawData: KeyMetricsData['rawData'],
  isProductEntity = false,
  productUtilizationData: UtilizationData[] = [],
  includeItemHighlights = false
): ContentUtilizationData[] => {
  const { mainKeyMetrics, comparisonKeyMetrics } = rawData;
  const mainMeta = mainKeyMetrics.metadataMetrics;
  const toPercent = (value: number) => value * 100;
  const template = getAmazonRetailerUtilizationTemplate(includeItemHighlights);
  const amazonRetailerTotalElements = Object.values(template).reduce((acc, curr) => acc + curr, 0);

  // business rule: normalize the number of images to 6
  const averageImages = safeDivide(
    (mainMeta?.imageMetrics?.actualCountNumerator || 0) * 6,
    mainMeta?.imageMetrics?.expectedCountDenominator || 1
  );

  // business rule: total content utilization score is based on missing elements, given the template
  let totalContentUtilizationDetails = `${Math.round(
    mainKeyMetrics?.totalProducts - mainKeyMetrics?.productsWithLowContentScore || 0
  ).toLocaleString()} ${isProductEntity ? '' : `(of ${Math.round(mainKeyMetrics?.totalProducts || 0).toLocaleString()})`
    } products have high overall utilization`;
  if (isProductEntity) {
    const missingElementsAnalysis = getMissingUtilizationElements(
      productUtilizationData,
      includeItemHighlights
    );
    totalContentUtilizationDetails = `${amazonRetailerTotalElements - missingElementsAnalysis.missingCount} ${isProductEntity ? '' : `(out of ${amazonRetailerTotalElements})`
      } content elements utilized`;
  }

  const titleMaxCharacters = Math.round(
    (mainMeta?.titleMetrics as LengthBasedMetrics)?.maxCharactersDenominator || 0
  );
  const titleEntityLength = Math.round(
    (mainMeta?.titleMetrics as LengthBasedMetrics)?.entityLengthNumerator || 0
  );
  const overLimitProducts =
    ('productsWithTitleOverLimit' in mainKeyMetrics
      ? mainKeyMetrics.productsWithTitleOverLimit
      : 0) || 0;
  // Product mock: over-limit keeps score at 100%; callout shows actual length + overage
  // (e.g. "101 characters used — 26 over the 75 limit"). Prefer product detail metrics
  // because summary SQL LEASTs the numerator at the cap.
  const titleSection = productUtilizationData.find(
    (item) => item.title === ContentUtilizationType.Title
  );
  const titleUsed = Math.round(Number(titleSection?.currentUtilization) || 0);
  const titleMax = Math.round(Number(titleSection?.maxUtilization) || titleMaxCharacters);
  const isProductTitleOverLimit =
    isProductEntity &&
    (titleSection?.isOverLimit === true ||
      (titleMax > 0 && titleUsed > titleMax) ||
      overLimitProducts > 0);
  const titleDetails =
    isProductTitleOverLimit && titleMax > 0 && titleUsed > titleMax
      ? `${titleUsed.toLocaleString()} characters used — ${(titleUsed - titleMax).toLocaleString()} over the ${titleMax.toLocaleString()} limit`
      : `${titleEntityLength.toLocaleString()} (of ${titleMaxCharacters.toLocaleString()}) title characters are utilized${
          isProductEntity ? '' : ' on avg'
        }`;

  const rows: ContentUtilizationData[] = [
    {
      title: ContentUtilizationTitles.Title,
      primary: toPercent(mainKeyMetrics.titleScore || 0),
      secondary: toPercent(comparisonKeyMetrics.titleScore || 0),
      details: titleDetails,
      lowProducts: mainKeyMetrics.productsWithLowTitleScore || 0,
      overLimitProducts,
      preferredTitleLink:
        overLimitProducts >= (mainKeyMetrics.productsWithLowTitleScore || 0)
          ? 'overLimit'
          : 'lowUtilization',
      scrollToId: ContentSectionId.Title
    }
  ];

  if (includeItemHighlights) {
    rows.push({
      title: ContentUtilizationTitles.ItemHighlights,
      primary: toPercent(mainKeyMetrics.itemHighlightsScore || 0),
      secondary: toPercent(comparisonKeyMetrics.itemHighlightsScore || 0),
      details: `${Math.round(
        mainMeta?.itemHighlightsMetrics?.entityLengthNumerator || 0
      ).toLocaleString()} (of ${(
        mainMeta?.itemHighlightsMetrics?.maxCharactersDenominator || 0
      ).toLocaleString()}) item highlight characters are utilized${isProductEntity ? '' : ' on avg'}`,
      lowProducts: mainKeyMetrics.productsWithLowItemHighlightsScore || 0,
      scrollToId: ContentSectionId.ItemHighlights
    });
  }

  rows.push(
    {
      title: ContentUtilizationTitles.Bullets,
      primary: toPercent(mainKeyMetrics.featureBulletsScore || 0),
      secondary: toPercent(comparisonKeyMetrics.featureBulletsScore || 0),
      details: `${Math.round(mainMeta?.bulletPointsMetrics?.entityLengthNumerator || 0).toLocaleString()} (of ${(
        mainMeta?.bulletPointsMetrics.maxCharactersDenominator || 0
      ).toLocaleString()}) bullet point characters are utilized${isProductEntity ? '' : ' on avg'}`,
      lowProducts: mainKeyMetrics.productsWithLowBulletPointsScore || 0,
      scrollToId: ContentSectionId.Bullets
    },
    {
      title: ContentUtilizationTitles.Descriptions,
      primary: toPercent(mainKeyMetrics.descriptionScore || 0),
      secondary: toPercent(comparisonKeyMetrics.descriptionScore || 0),
      details: `${Math.round(
        mainMeta?.descriptionMetrics?.entityLengthNumerator || 0
      ).toLocaleString()} (of ${Math.round(
        mainMeta?.descriptionMetrics?.maxCharactersDenominator || 0
      ).toLocaleString()}) description characters are utilized${isProductEntity ? '' : ' on avg'}`,
      lowProducts: mainKeyMetrics.productsWithLowDescriptionScore || 0,
      scrollToId: ContentSectionId.Descriptions
    },
    {
      title: ContentUtilizationTitles.Images,
      primary: toPercent(mainKeyMetrics.imagesScore || 0),
      secondary: toPercent(comparisonKeyMetrics.imagesScore || 0),
      details: `${(averageImages || 0).toFixed(1)} (of 6) image placements are utilized${isProductEntity ? '' : ' on avg'
        }`,
      lowProducts: mainKeyMetrics.productsWithLowImageScore || 0,
      scrollToId: ContentSectionId.Images
    },
    {
      title: ContentUtilizationTitles.Videos,
      primary: toPercent(mainKeyMetrics.videosScore || 0),
      secondary: toPercent(comparisonKeyMetrics.videosScore || 0),
      details: `${Math.round(mainMeta?.videoMetrics?.actualCountNumerator || 0).toLocaleString()} (of ${Math.round(
        mainMeta?.videoMetrics?.expectedCountDenominator || 0
      ).toLocaleString()})${isProductEntity ? ' videos placements are utilized' : ' products utilize videos'}`,
      lowProducts: mainKeyMetrics.productsWithLowVideoScore || 0,
      scrollToId: ContentSectionId.Videos
    },
    {
      title: ContentUtilizationTitles.Enhanced,
      primary: toPercent(mainKeyMetrics.aPlusScore || 0),
      secondary: toPercent(comparisonKeyMetrics.aPlusScore || 0),
      details: `${Math.round(mainMeta?.aPlusMetrics?.actualCountNumerator || 0).toLocaleString()} (of ${Math.round(
        mainMeta?.aPlusMetrics?.expectedCountDenominator || 0
      ).toLocaleString()})${isProductEntity ? ' enhanced content utilized' : ' products utilized enhanced content'}`,
      lowProducts: mainKeyMetrics.productsWithLowAPlusScore || 0,
      scrollToId: ContentSectionId.Enhanced
    },
    {
      title: ContentUtilizationTitles.TotalContentUtilization,
      primary: toPercent(mainKeyMetrics.contentUtilizationScore || 0),
      secondary: toPercent(comparisonKeyMetrics.contentUtilizationScore || 0),
      details: totalContentUtilizationDetails,
      lowProducts: mainKeyMetrics.productsWithLowContentScore || 0,
      scrollToId: ContentSectionId.Title
    }
  );

  return rows;
};

export type FormattedContentData = ReturnType<typeof formatContentUtilizationData>;
